import { z } from 'zod';
import {
  evidenceSchema,
  milestoneSchema,
  completionSubmissionSchema,
} from './current-project';

const id = milestoneSchema.shape.milestoneId;
const account = completionSubmissionSchema.shape.submittedBy;
const instant = completionSubmissionSchema.shape.submittedAt;
export const disputeViewSchema = z
  .object({
    dispute: z.object({
      disputeId: id,
      projectId: id,
      milestoneId: id,
      rejectedSubmissionId: id,
      status: z.literal('Open'),
      openedBy: account,
      counterparty: account,
      openedAt: instant,
      evidence: evidenceSchema.nullable(),
      openingCommentId: id.optional(),
      response: z
        .object({
          evidence: evidenceSchema,
          author: account,
          occurredAt: instant,
        })
        .nullable(),
      proposalRevision: instant,
      contextEventCursor: instant,
    }),
    milestone: milestoneSchema,
  })
  .refine(
    ({ dispute, milestone }) =>
      dispute.milestoneId === milestone.milestoneId &&
      milestone.taskStorage.milestoneId === milestone.milestoneId &&
      milestone.status === 'Disputed' &&
      milestone.frozen &&
      dispute.openedBy !== dispute.counterparty &&
      Boolean(dispute.evidence) !== Boolean(dispute.openingCommentId) &&
      (!dispute.response || dispute.response.author === dispute.counterparty) &&
      milestone.submissions[milestone.submissions.length - 1]?.submissionId ===
        dispute.rejectedSubmissionId &&
      (milestone.submissions[milestone.submissions.length - 1]?.review.type ===
        'Rejected' ||
        milestone.submissions[milestone.submissions.length - 1]?.review.type ===
          'RejectedWithComment'),
    'The public case context does not match its dispute.'
  );
export type DisputeView = z.infer<typeof disputeViewSchema>;

export const disputeAuthorSchema = z.object({
  accountId: account,
  displayName: z.string().nullable(),
  imageUrl: z.string().nullable(),
  role: z.enum(['client', 'consultant', 'worker']),
});
export const disputePresentationSchema = z.object({
  projectTitle: z.string(),
  openingReason: z.string(),
  client: disputeAuthorSchema,
  coordinator: disputeAuthorSchema,
});
export const disputeEntrySchema = z.object({
  cursor: z.number().int().positive().safe(),
  entryId: id,
  disputeId: id,
  author: disputeAuthorSchema,
  createdAt: instant,
  content: z.string(),
  argumentType: z.enum(['RESPONSE', 'ADDITIONAL', 'MESSAGE']),
});
export const disputeTextSchema = z
  .string()
  .refine(
    (value) =>
      !!value.trim() &&
      !value.includes('\0') &&
      new TextEncoder().encode(value).length <= 4000,
    'Enter text between 1 and 4000 UTF-8 bytes.'
  );
